<script lang="ts">
	import Button from '$lib/components/util/Button.svelte';
	import Input from '$lib/components/inputs/Input.svelte';
	import { page } from '$app/stores';
	import Text from '$lib/components/Text.svelte';
	import { showLocationOptions } from '$lib/stores/shopStore';
	import GlobalMedia from '../media/GlobalMedia.svelte';
	import DirectusLink from '../navigation/DirectusLink.svelte';
	import Accordion from '../util/Accordion.svelte';
	import LangSwitcher from '../util/LangSwitcher.svelte';
	import LocationsSwitcherBar from '../util/LocationsSwitcherBar.svelte';
	import LocationSwitcher from '../util/LocationSwitcher.svelte';
	import TextWithIcon from '../util/TextWithIcon.svelte';
	import Section from './Section.svelte';
	import Media from '../media/Media.svelte';
	import { getText } from '$lib/util/translations';
	import Loader from '../util/Loader.svelte';
	import NewsletterForm from '../util/NewsletterForm.svelte';

	let loading: boolean = false;

	// Fetch only menus where the internal name starts with 'footer'
	$: footerMenus = $page.data.menus.filter((menu: any) =>
		menu?.internal_name?.startsWith('footer')
	);

	let mobileSocialMediaIcons: any;
	$: mobileSocialMediaIcons = $page.data.menus.find(
		(menu: any) => menu.internal_name === 'mobile_social_media_icons'
	);

	// Function to filter links based on internal name
	function filterLinks(internalName: string) {
		const menu = footerMenus.find((menu: any) => menu.internal_name === internalName);
		return menu ? menu.links : [];
	}

	function setCookie(cname: string, cvalue: string, exdays: number) {
		const d = new Date();
		d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
		let expires = 'expires=' + d.toUTCString();
		document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
	}

	function selectLocation(location: string) {
		showLocationOptions.set(false);
		setCookie('location', location, 365);
		window.location.reload();
	}
	interface AccordionItem {
		title: string;
		text: string;
		iconType: 'arrow';
	}

	let accordionItems: Array<AccordionItem> = [];
	$: accordionItems = footerMenus
		.map((menu) => ({
			title: menu.title ?? '',
			links: filterLinks(menu.internal_name),
			iconType: 'arrow'
		}))
		.slice(0, -1);

	async function registerNewsletter() {
		success = false;
		error = false;
		loading = true;

		const res = await fetch('/api/newsletter-klaviyo', {
			method: 'POST',
			body: JSON.stringify({
				firstname,
				lastname,
				email
			})
		});

		let data = await res.json();
		data = JSON.parse(data.data);
		data = JSON.parse(data[0]);

		if (data.success) {
			success = true;
			email = '';
			firstname = '';
			lastname = '';
		} else {
			error = true;
			if (data.error) {
				errorMsg = data.error;
			}
		}
		loading = false;
	}

	let success: boolean = false;
	let error: boolean = false;
	let errorMsg: string;
	let email: string = '';
	let firstname: string = '';
	let lastname: string = '';
</script>

<footer>
	<Section gapType="padding">
		<div class="footer-wrapper">
			<div class="footer-menu">
				<div class="col1">
					<div class="logo">
						<a href="/">
							<GlobalMedia key="logo-text" />
						</a>
					</div>

					<NewsletterForm />
				</div>
				<div class="col2">
					<div class="title">
						<Text key="footer_title" />
					</div>
					<div class="mobile-footer">
						<Accordion items={accordionItems} />
						<div class="icons-wrapper">
							{#each mobileSocialMediaIcons.links as link}
								<DirectusLink {...link.link_id}>
									<TextWithIcon
										{...link.link_id}
										color="yellow"
										iconHeight="24px"
										iconWidth="24px"
									/>
								</DirectusLink>
							{/each}
						</div>
					</div>
					<div class="grid">
						{#each footerMenus as menu}
							<div>
								<div class="menu-name">{menu.title ?? ''}</div>
								{#each filterLinks(menu.internal_name) as link}
									<div class="links-wrapper" id="links-wrapper">
										<DirectusLink {...link.link_id}>
											<TextWithIcon {...link.link_id} iconPos="before" />
										</DirectusLink>
									</div>
								{/each}
							</div>
						{/each}

						<div class="footer-icon">
							<GlobalMedia key="footer-icon" />
						</div>
					</div>
					<div class="bottom">
						<div class="switchers">
							<LangSwitcher />
							<LocationSwitcher
								on:click={() => {
									showLocationOptions.set(!$showLocationOptions);
								}}
							/>
						</div>
						<div class="cards">
							<Text key="we_accept" />
							<div class="payment-methods">
								{#each $page.data.globals.payment_methods ?? [] as { directus_files_id: item }}
									<div class="payment-method">
										<Media {...item} transform="xl" />
									</div>
								{/each}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Section>
</footer>
<LocationsSwitcherBar
	open={$showLocationOptions}
	on:close={() => {
		showLocationOptions.set(false);
	}}
	on:onlocationchange={(event) => {
		selectLocation(event.detail.location);
	}}
/>

<style lang="scss">
	footer {
		background: color(black);
		:global(section) {
			margin: 0 !important;
		}
	}

	.footer-menu {
		color: color(yellow);
		display: grid;
		grid-template-columns: 1.5fr 5fr;
		gap: v(content-gap-2xl);
		@media (min-width: 1800px) {
			grid-template-columns: 1fr 5fr;
		}
		@media (max-width: 1200px) {
			grid-template-columns: 1fr;
		}
	}
	.col1 {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		position: relative;
		overflow: visible;

		@media (max-width: 1200px) {
			flex-direction: row-reverse;
		}
	}
	.grid {
		display: grid;
		grid-template-columns: repeat(5, minmax(0, 1fr));
		gap: v(content-gap);
		border-top: solid 1px color(yellow);
		border-bottom: solid 1px color(yellow);
		padding: v(content-gap) 0;
		position: relative;
	}
	.title {
		@include font(h3);
		margin-bottom: v(content-gap);
	}
	.logo {
		height: 60px;
		width: 100px;
		display: flex;
		justify-content: flex-start;
		align-items: flex-start;
	}
	.menu-name {
		font-weight: bold;
		text-transform: uppercase;
		@include font(h6);
	}
	.footer-icon {
		position: absolute;
		bottom: 10px;
		inset-inline-end: 0;
		width: 80px;
		height: 80px;
	}

	.bottom {
		width: 100%;
		display: flex;
		margin-top: v(content-padding);
		justify-content: space-between;

		@media (max-width: 1200px) {
			flex-direction: column;
			gap: v(content-gap);
		}
		:global(.lang-label),
		:global(.location-label) {
			@include font(text-sm);
		}
	}
	.switchers {
		display: flex;
		width: 100%;
		gap: v(content-gap);
		@media (max-width: 1200px) {
			padding-bottom: v(content-gap);
			border-bottom: solid 1px color(yellow);
			justify-content: center;
		}
	}
	.cards {
		display: flex;
		gap: v(content-gap-xs);
		white-space: nowrap;
		align-items: center;
		@media (max-width: 1200px) {
			display: flex;
			justify-content: center;
		}
		@include media(phone) {
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
		}
	}
	.links-wrapper {
		:global(.text-with-icon) {
			padding-bottom: v(content-gap-xs);
			color: color(yellow) !important;
		}

		:global(svg path) {
			stroke: color(yellow) !important;
			fill: color(yellow) !important;
		}

		transition: all 0.3s ease;
		&:hover {
			opacity: 0.5 !important;
		}
	}
	#links-wrapper {
		:global(.text-with-icon) {
			@include font(text-sm);
		}
		:global(a) {
			@include font(text-sm);
		}
	}
	.menu-name {
		margin-bottom: v(content-gap);
	}
	.mobile-footer {
		display: none;
		:global(.text-with-icon) {
			color: color(yellow);
			border-bottom: none;
			padding-bottom: v(content-gap-xs);
			&:hover {
				border-bottom: none !important;
			}
		}
	}
	.switchers {
		:global(.switcher-dropdown) {
			transform: translate(-50%, -180%) !important;
		}
	}
	.payment-methods {
		width: auto;
		display: flex;
		gap: v(content-gap-2xs);
		justify-content: center;

		.payment-method {
			border-radius: v(border-radius-sm);
			background-color: color(white);
			display: flex;
			align-items: center;
			:global(svg),
			:global(img) {
				width: auto;
				height: auto;
				max-height: 2rem;
				max-width: 100%;

				@include media(tablet) {
					max-height: 1.5rem;
					max-width: 30px;
				}
			}
		}
		@include media(phone) {
			justify-content: flex-start;
			flex-wrap: wrap;
		}
	}
	@include media(phone) {
		// footer {
		// 	padding: v(se-padding);
		// }
		.grid {
			display: none;
		}
		.mobile-footer {
			display: flex;
			border-top: solid 1px color(yellow);
			border-bottom: solid 1px color(yellow);
			padding-top: v(content-padding);
			flex-direction: column;
		}
		.icons-wrapper {
			display: flex;
			padding: v(content-gap-lg) 0 v(content-gap) 0;
			gap: v(content-gap);
		}
		.logo {
			justify-content: center;
		}

		.col1 {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
		}
		.subscription-wrapper {
			align-items: center;
			height: 100%;
		}
		.footer-menu {
			display: flex;

			flex-direction: column;
			justify-content: center;
			gap: v(content-gap-xl);
		}
		.switchers,
		.cards {
			justify-content: flex-start;
		}
	}

	.loader-wrapper {
		height: 16px;
		margin: 0 !important;

		:global(.loader-wrapper) {
			margin-top: 0;
		}
	}
</style>
